import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Card, Button, Form } from "react-bootstrap";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from '../../common/Toast/toast';
import PageTitle from "../../common/PageTitle.jsx";
import { getAssignModules } from "../../../../services/AssignModulesService.js";
import {
  createOperationToModule,
  getOperationToModuleById,
  updateOperationToModule,
} from "../../../../services/OMService.js";
import { getOperations } from "../../../../services/OperationService.js";
import "../../common/SettingsStepForm.css";

// Validation schema
/* Step one's rules on their own, and the whole form's built from them,
   so "may Continue?" is answered by the same rules that decide "may
   Save?" -- a hand-written copy of them on the button is a second,
   quieter schema that drifts the moment a rule is added here. */
const stepOneSchema = Yup.object().shape({
  label: Yup.string().trim().required("Label is required"),
  value: Yup.string().trim().required("Value is required"),
  moduleId: Yup.string().required("Module is required"),
});

const validationSchema = stepOneSchema.shape({
  operationIds: Yup.array().min(1, "At least one operation is required"),
});

/* ── The two steps ──────────────────────────────────────────────────
   Four fields, and the last of them is a picker long enough to be the
   whole dialog if it shares a screen with the other three. Split the
   way AddAttendance splits its own, and the way the app-to-role dialog
   does. The dialog titles itself from this, through onStepChange.
   ------------------------------------------------------------------ */
const STEP_COPY = [
  {
    title: "What is it, and which module?",
    hint: "A name, the key permission checks are written against, and the module this belongs to.",
  },
  {
    title: "Which operations does it allow?",
    hint: "Tick every operation this module grants.",
  },
];

const STEP_LABELS = ["Details", "Operations"];

// The rule that turns a label into its key. Named because three places
// have to agree on it: the label's onChange, the "use the generated one"
// link, and the check for whether the key is still the derived one.
const slugify = (label) =>
  String(label || "").toLowerCase().trim().replace(/\s+/g, "-");

const idOf = (row) => String(row?._id || row?.id || "");

const TickIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const WarnIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16.5v.01" />
  </svg>
);

// Color theme
/* The palette's own tokens rather than a second theme of this
   screen's own. These were literal oranges, so these pages and their
   popups stayed orange whichever palette was chosen while the app
   around them moved. ThemeContext writes the --pg-* custom properties
   onto the document, and a custom property works in an inline style
   exactly as it does in a stylesheet, so naming them here is the whole
   of the fix.

   Ink is deliberately not a palette value (see css/theme/palettes.css):
   it holds its contrast under every palette, so it stays literal. */
const THEME = {
  primary: 'var(--pg-accent)',
  primaryDark: 'var(--pg-accent-hover)',
  cardBg: 'var(--pg-card)',
  border: 'var(--pg-line)',
};

// Filter Section Component
const FilterSection = ({ title, children }) => (
  <div
    className="filter-section mb-4 p-4 bg-dark"
    style={{
      borderRadius: "15px",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      // background: THEME.cardBg,
      border: `1px solid ${THEME.border}`,
    }}
  >
    <h5
      className="mb-3"
      style={{
        borderBottom: `2px solid ${THEME.primary}`,
        paddingBottom: "8px",
        color: THEME.primaryDark,
      }}
    >
      {title}
    </h5>
    {children}
  </div>
);

function AddOM({
  // Shown inside a dialog rather than as a page: the component drops its
  // own back-link and toast host, since the dialog and the list supply
  // them. The card chrome around it is flattened by SettingsFormModal.css.
  embedded = false,
  // The record to edit. Falls back to the :id route param.
  recordId,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
  // Told the current step's question, so the dialog can title itself.
  onStepChange,
}) {
  const navigate = useNavigate();

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded. Every exit goes through these two so the
  // component never has to know which it is.
  const leave = () => {
    if (onCancel) onCancel();
    else navigate('/setting/operationstomodules');
  };

  const finish = () => {
    if (onDone) onDone();
    else navigate('/setting/operationstomodules');
  };
  const { id: routeId } = useParams();
  const id = recordId ?? routeId;
  const [omData, setOmData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [operations, setOperations] = useState([]);
  const formikBagRef = useRef(null);

  /* ── Steps ────────────────────────────────────────────────────────
     `step` is the one being asked for; `shownStep` is the one on
     screen, which trails it by one exit. Keying the stage on `step`
     alone swaps the content in a single frame -- the old step vanishes
     with no exit while the new one animates in from nothing, which is
     what reads as a jump. Same pair AddAttendance keeps.
     ---------------------------------------------------------------- */
  const [step, setStep] = useState(1);
  const [shownStep, setShownStep] = useState(1);
  const [stepPhase, setStepPhase] = useState("in");
  // What the picker's search box holds. Lives here rather than in the
  // step so it survives a trip back to step one.
  const [pickQuery, setPickQuery] = useState("");

  useEffect(() => {
    if (shownStep === step) return undefined;
    setStepPhase("out");
    const t = setTimeout(() => {
      setShownStep(step);
      setStepPhase("in");
    }, 160);
    return () => clearTimeout(t);
  }, [step, shownStep]);

  useEffect(() => {
    if (onStepChange) onStepChange(STEP_COPY[step - 1], step);
    // onStepChange is the dialog's setState; adding it here would
    // re-report on every render of the list behind it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Initial form values
  const initialValues = {
    label: "",
    value: "",
    moduleId: "",
    operationIds: [],
  };

  // Fetch modules for dropdown
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const res = await getAssignModules(1000, 1, "");
        setModules(res.data || []);
      } catch (error) {
        toast.error("Failed to fetch modules");
      }
    };
    fetchModules();
  }, []);

  // Fetch operations for selection
  useEffect(() => {
    const fetchOps = async () => {
      try {
        const res = await getOperations(1000, 1, "");
        setOperations(res.data || []);
      } catch (error) {
        toast.error("Failed to fetch operations");
      }
    };
    fetchOps();
  }, []);

  // Fetch OM details for editing
  const fetchOMDetails = async (omId) => {
    try {
      const response = await getOperationToModuleById(omId);
      const data = response.data;
      if (data) {
        setOmData(data);
        setIsEditing(true);
        if (formikBagRef.current) {
          let opIds = [];
          if (Array.isArray(data.operationIds)) {
            // If array of objects or strings
            opIds = data.operationIds.map((op) =>
              typeof op === "object" ? op._id || op.id : op
            );
          } else if (typeof data.operationIds === "string") {
            opIds = data.operationIds.split(",");
          }
          formikBagRef.current.setValues({
            label: data.label || "",
            value: data.value || "",
            moduleId: data.moduleId || "",
            operationIds: opIds,
          });
        }
        return data;
      } else {
        setOmData(null);
        setIsEditing(false);
        return null;
      }
    } catch (error) {
      toast.error("Failed to fetch details");
      return null;
    }
  };

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        if (id) {
          await fetchOMDetails(id);
        } else {
          setOmData(null);
          setIsEditing(false);
        }
      } catch (error) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    initializeData();
  }, [id]);

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const payload = {
        label: values.label,
        value: values.value,
        moduleId: values.moduleId,
        operationIds: values.operationIds.join(","), // comma-separated string of IDs
      };
      if (isEditing) {
        await updateOperationToModule(id, payload);
        toast.success("Operation to Module updated successfully!");
      } else {
        await createOperationToModule(payload);
        toast.success("Operation to Module added successfully!");
      }
      resetForm();
      setOmData(null);
      setIsEditing(false);
      finish();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Error processing!";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "20px" }}>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "200px" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  /* ── The dialog build ───────────────────────────────────────────────
     Two steps, the shape AddAttendance uses and the app-to-role dialog
     shares. The stepper, the stage and its transition are the same
     `att-*` classes; everything inside a step is the `sf-*` language in
     common/SettingsStepForm.css.
     ------------------------------------------------------------------ */
  if (embedded) {
    return (
      <div className="sf">
        <Formik
          innerRef={formikBagRef}
          initialValues={
            omData
              ? {
                  label: omData.label || "",
                  value: omData.value || "",
                  moduleId: omData.moduleId || "",
                  operationIds: Array.isArray(omData.operationIds)
                    ? omData.operationIds.map((op) =>
                        typeof op === "object" ? idOf(op) : String(op)
                      )
                    : String(omData.operationIds || "")
                        .split(",")
                        .filter(Boolean),
                }
              : initialValues
          }
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, setFieldTouched, submitForm, isSubmitting, errors, touched }) => {
            const derived = slugify(values.label);
            // The key still matches what the label would generate, so it
            // can go on tracking it. Once it does not, the label stops
            // overwriting it -- a typed key is the reader's, not ours.
            const isAuto = values.value === derived;
            const labelBad = touched.label && errors.label;
            const valueBad = touched.value && errors.value;
            const moduleBad = touched.moduleId && errors.moduleId;
            const opsBad = touched.operationIds && errors.operationIds;

            const selected = new Set(values.operationIds.map(String));
            const shown = pickQuery.trim()
              ? operations.filter((op) =>
                  String(op.label || "")
                    .toLowerCase()
                    .includes(pickQuery.trim().toLowerCase())
                )
              : operations;

            const toggle = (oid) => {
              setFieldTouched("operationIds", true, false);
              setFieldValue(
                "operationIds",
                selected.has(oid)
                  ? values.operationIds.filter((v) => String(v) !== oid)
                  : [...values.operationIds, oid]
              );
            };

            // Bulk actions act on what the search is showing, not on the
            // whole list -- "Select all" under a filter that hides most of
            // it, silently ticking the hidden ones, is a trap.
            const shownIds = shown.map(idOf);
            const allShownOn =
              shownIds.length > 0 && shownIds.every((oid) => selected.has(oid));

            const selectShown = () => {
              setFieldTouched("operationIds", true, false);
              setFieldValue("operationIds", [
                ...new Set([...values.operationIds.map(String), ...shownIds]),
              ]);
            };

            const clearShown = () => {
              setFieldTouched("operationIds", true, false);
              const drop = new Set(shownIds);
              setFieldValue(
                "operationIds",
                values.operationIds.filter((v) => !drop.has(String(v)))
              );
            };

            // Step one is answered when its own fields pass their own
            // rules -- the same object the full schema is built from, so
            // there is no second copy of them here to fall out of step.
            const stepOneOk = stepOneSchema.isValidSync({
              label: values.label,
              value: values.value,
              moduleId: values.moduleId,
            });

            const goToTwo = () => {
              setFieldTouched("label", true, true);
              setFieldTouched("value", true, true);
              setFieldTouched("moduleId", true, true);
              if (stepOneOk) setStep(2);
            };

            const save = () => {
              setFieldTouched("operationIds", true, true);
              // Saving with a step-one field invalid would fail validation
              // silently -- its error lives on a step that is not on
              // screen. Go back and show it.
              if (!stepOneOk) {
                setStep(1);
                return;
              }
              submitForm();
            };

            return (
              <Form
                noValidate
                onSubmit={(e) => {
                  // Nothing in here is type="submit", so the only thing
                  // that reaches this is Enter pressed inside a field. On
                  // step one that key means "continue".
                  e.preventDefault();
                  if (step !== 2) {
                    goToTwo();
                    return;
                  }
                  save();
                }}
              >
                <ol className="att-steps">
                  {STEP_LABELS.map((label, i) => {
                    const n = i + 1;
                    const state = n === step ? "is-now" : n < step ? "is-done" : "";
                    return (
                      <li className={`att-step-tab ${state}`.trim()} key={label}>
                        <button
                          type="button"
                          // Adding: only backwards, a step ahead has not been
                          // answered yet. Editing: every step is filled, so any tab opens.
                          disabled={n === step || (!isEditing && n > step)}
                          onClick={() => setStep(n)}
                        >
                          <span className="att-step-n">{n}</span>
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ol>

                {/* Painted from shownStep, not step -- see the comment on
                    the transition effect above. */}
                <div className={`att-stage is-${stepPhase}`}>
                  <div className="att-stage-inner" key={shownStep}>
                    {shownStep === 1 && (
                      <section className="att-step">
                        <p className="sf-step-note">
                          The name is what you will recognise this by; the key is
                          what the code checks.
                        </p>

                        <div className="sf-field">
                          <div className="sf-label-row">
                            <label className="sf-label" htmlFor="otm-label">
                              Label<span className="sf-req">*</span>
                            </label>
                          </div>
                          <input
                            id="otm-label"
                            type="text"
                            className={`sf-input${labelBad ? " sf-input--bad" : ""}`}
                            value={values.label}
                            placeholder="Manage batches, View reports…"
                            autoComplete="off"
                            onChange={(e) => {
                              const next = e.target.value;
                              setFieldValue("label", next);
                              // Only while the key is still the generated one.
                              if (isAuto) setFieldValue("value", slugify(next));
                            }}
                            onBlur={() => setFieldTouched("label", true, true)}
                          />
                          {labelBad ? (
                            <p className="sf-error"><WarnIcon />{errors.label}</p>
                          ) : (
                            <p className="sf-hint">
                              How this reads wherever permissions are listed.
                            </p>
                          )}
                        </div>

                        <div className="sf-field sf-derived">
                          <div className="sf-label-row">
                            <label className="sf-label" htmlFor="otm-value">
                              Value<span className="sf-req">*</span>
                              {isAuto ? (
                                <span className="sf-state sf-state--auto">From label</span>
                              ) : (
                                <>
                                  <span className="sf-state sf-state--custom">Edited</span>
                                  {derived && (
                                    <button
                                      type="button"
                                      className="sf-relink"
                                      onClick={() => setFieldValue("value", derived)}
                                    >
                                      use {derived}
                                    </button>
                                  )}
                                </>
                              )}
                            </label>
                          </div>
                          <input
                            id="otm-value"
                            type="text"
                            className={`sf-input sf-input--slug${valueBad ? " sf-input--bad" : ""}`}
                            value={values.value}
                            placeholder="manage-batches"
                            autoComplete="off"
                            spellCheck="false"
                            onChange={(e) =>
                              setFieldValue("value", e.target.value.replace(/\s+/g, "-"))
                            }
                            onBlur={() => setFieldTouched("value", true, true)}
                          />
                          {valueBad ? (
                            <p className="sf-error"><WarnIcon />{errors.value}</p>
                          ) : (
                            <p className="sf-hint">
                              The key permission checks are written against. Changing
                              it on an entry already in use breaks those checks.
                            </p>
                          )}
                        </div>

                        <div className="sf-field">
                          <div className="sf-label-row">
                            <label className="sf-label" htmlFor="otm-module">
                              Module<span className="sf-req">*</span>
                            </label>
                          </div>
                          <select
                            id="otm-module"
                            className={`sf-input${moduleBad ? " sf-input--bad" : ""}`}
                            value={values.moduleId}
                            onChange={(e) => setFieldValue("moduleId", e.target.value)}
                            onBlur={() => setFieldTouched("moduleId", true, true)}
                          >
                            <option value="">Select a module…</option>
                            {modules.map((mod) => (
                              <option key={idOf(mod)} value={idOf(mod)}>
                                {mod.label}
                              </option>
                            ))}
                          </select>
                          {moduleBad ? (
                            <p className="sf-error"><WarnIcon />{errors.moduleId}</p>
                          ) : (
                            <p className="sf-hint">
                              The section of the application these operations belong to.
                            </p>
                          )}
                        </div>
                      </section>
                    )}

                    {shownStep === 2 && (
                      <section className="att-step">
                        <p className="sf-step-note">
                          Every operation ticked here is granted on the module
                          chosen in the previous step.
                        </p>

                        <div className="sf-pick-bar">
                          <div className="sf-search">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                              <circle cx="11" cy="11" r="7" />
                              <path d="m20 20-3.5-3.5" />
                            </svg>
                            <input
                              type="text"
                              className="sf-search-input"
                              value={pickQuery}
                              placeholder="Search operations…"
                              autoComplete="off"
                              onChange={(e) => setPickQuery(e.target.value)}
                            />
                          </div>

                          <button
                            type="button"
                            className="sf-bulk"
                            onClick={allShownOn ? clearShown : selectShown}
                            disabled={shownIds.length === 0}
                          >
                            {allShownOn ? "Clear" : "Select"}
                            {pickQuery.trim() ? " shown" : " all"}
                          </button>
                        </div>

                        <p className="sf-tally">
                          <b>{values.operationIds.length}</b> of {operations.length} selected
                          {pickQuery.trim() && ` · ${shown.length} shown`}
                        </p>

                        {shown.length === 0 ? (
                          <div className="sf-empty">
                            {operations.length === 0
                              ? "No operations exist yet."
                              : `Nothing matches “${pickQuery.trim()}”.`}
                          </div>
                        ) : (
                          <ul className="sf-picklist">
                            {shown.map((op) => {
                              const oid = idOf(op);
                              const on = selected.has(oid);
                              return (
                                <li key={oid}>
                                  <button
                                    type="button"
                                    className={`sf-pick${on ? " is-on" : ""}`}
                                    aria-pressed={on}
                                    onClick={() => toggle(oid)}
                                  >
                                    <span className="sf-box" aria-hidden="true">
                                      <TickIcon />
                                    </span>
                                    <span className="sf-pick-name">{op.label}</span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}

                        {opsBad && (
                          <p className="sf-error"><WarnIcon />{errors.operationIds}</p>
                        )}
                      </section>
                    )}
                  </div>
                </div>

                <footer className="sf-foot">
                  {/* Keyed, and neither is type="submit". Two buttons at one
                      position are otherwise reconciled onto a single DOM
                      node: clicking Continue would flush step=2 before the
                      browser reached the click's default action, by which
                      time that same node was a submit button inside a form
                      -- and the record saved from step one. */}
                  {step > 1 ? (
                    <button key="back" type="button" className="sf-btn" onClick={() => setStep(1)}>
                      Back
                    </button>
                  ) : (
                    <button key="cancel" type="button" className="sf-btn" onClick={leave}>
                      Cancel
                    </button>
                  )}

                  <span className="sf-foot-gap" />

                  {step === 1 ? (
                    <button
                      key="continue"
                      type="button"
                      className="sf-btn sf-btn--main"
                      onClick={goToTwo}
                      disabled={!stepOneOk}
                    >
                      Continue
                    </button>
                  ) : (
                    <button
                      key="save"
                      type="button"
                      className="sf-btn sf-btn--main"
                      disabled={isSubmitting || values.operationIds.length === 0}
                      onClick={save}
                    >
                      {isSubmitting && <span className="sf-spin" aria-hidden="true" />}
                      {isSubmitting
                        ? isEditing ? "Updating…" : "Saving…"
                        : isEditing ? "Update mapping" : "Create mapping"}
                    </button>
                  )}
                </footer>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }

  return (
    <>
      {/* Page-only: the list behind the dialog already has a toast host,
          and two would show every message twice. */}
      <div style={{ padding: "20px" }}>
        {/* The page's way back. The dialog has its own close button and a
            Cancel, and this one would navigate out from under it. */}
        {!embedded && (
          <Link to="/setting/operationstomodules">
            <Button
              style={{
                backgroundColor: THEME.primary,
                borderColor: THEME.primary,
                borderRadius: "30px",
                padding: "8px 20px",
              }}
              className="mb-5"
            >
              Go to Operations to Modules List
            </Button>
          </Link>
        )}
        <PageTitle
          pageTitle={
            isEditing ? "Edit Operation to Module" : "Add Operation to Module"
          }
        />

        <Formik
          enableReinitialize={true}
          initialValues={
            isEditing && omData
              ? {
                  label: omData.label || "",
                  value: omData.value || "",
                  moduleId: omData.moduleId || "",
                  operationIds: Array.isArray(omData.operationIds)
                    ? omData.operationIds
                    : (omData.operationIds || "").split(","),
                }
              : initialValues
          }
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formik) => {
            const { values, isSubmitting, setFieldValue, errors, touched } =
              formik;
            formikBagRef.current = formik;

            // Toggle operation selection by ID
            const toggleOperation = (opId) => {
              if (values.operationIds.includes(opId)) {
                setFieldValue(
                  "operationIds",
                  values.operationIds.filter((v) => v !== opId)
                );
              } else {
                setFieldValue("operationIds", [...values.operationIds, opId]);
              }
            };

            // Select All/Clear All
            const selectAll = () =>
              setFieldValue(
                "operationIds",
                operations.map((op) => op._id || op.id)
              );
            const clearAll = () => setFieldValue("operationIds", []);

            return (
              <Form onSubmit={formik.handleSubmit}>
                <Card
                  className="border-0 shadow-sm"
                  style={{ borderRadius: "15px", overflow: "hidden" }}
                >
                  <Card.Body className="bg-dark">
                    <FilterSection title="Operation to Module Details">
                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group controlId="formLabel">
                            <Form.Label style={{ color: THEME.primaryDark }}>
                              Label <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="label"
                              value={values.label}
                              onChange={(e) => {
                                const label = e.target.value;
                                const processed = label
                                  .toLowerCase()
                                  .replace(/\s+/g, "-");
                                setFieldValue("label", label);
                                setFieldValue("value", processed);
                              }}
                              placeholder="Enter label (e.g., add-user)"
                              isInvalid={touched.label && errors.label}
                            />
                            <ErrorMessage
                              name="label"
                              component="div"
                              className="text-danger"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="formValue">
                            <Form.Label style={{ color: THEME.primaryDark }}>
                              Value <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="value"
                              value={values.value}
                              onChange={(e) =>
                                setFieldValue(
                                  "value",
                                  e.target.value.replace(/\s+/g, "-")
                                )
                              }
                              placeholder="Enter value (e.g., add-user)"
                              isInvalid={touched.value && errors.value}
                            />
                            <ErrorMessage
                              name="value"
                              component="div"
                              className="text-danger"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group controlId="formModuleId">
                            <Form.Label style={{ color: THEME.primaryDark }}>
                              Module <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                              name="moduleId"
                              value={values.moduleId}
                              onChange={(e) =>
                                setFieldValue("moduleId", e.target.value)
                              }
                              isInvalid={touched.moduleId && errors.moduleId}
                            >
                              <option value="">Select Module</option>
                              {modules.map((mod) => (
                                <option
                                  key={mod._id || mod.id}
                                  value={mod._id || mod.id}
                                >
                                  {mod.label}
                                </option>
                              ))}
                            </Form.Select>
                            <ErrorMessage
                              name="moduleId"
                              component="div"
                              className="text-danger"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group controlId="formOperationIds">
                            <Form.Label style={{ color: THEME.primaryDark }}>
                              Operations <span className="text-danger">*</span>
                            </Form.Label>
                            <div className="mb-2 d-flex gap-2">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                onClick={selectAll}
                                style={{
                                  borderRadius: "20px",
                                  fontWeight: 500,
                                }}
                              >
                                Select All
                              </Button>
                              <Button
                                variant="outline-dark"
                                size="sm"
                                onClick={clearAll}
                                style={{
                                  borderRadius: "20px",
                                  fontWeight: 500,
                                }}
                              >
                                Clear All
                              </Button>
                            </div>
                            <div className="d-flex flex-wrap gap-3">
                              {operations.map((op) => (
                                <div
                                  key={op._id || op.id}
                                  className={`d-flex align-items-center px-3 py-2 rounded border ${
                                    values.operationIds.includes(
                                      op._id || op.id
                                    )
                                      ? "border-warning bg-warning bg-opacity-10"
                                      : "border-warning bg-white"
                                  }`}
                                  style={{
                                    minWidth: 120,
                                    marginBottom: 8,
                                    borderWidth: 2,
                                    cursor: "pointer",
                                    transition: "background 0.2s",
                                  }}
                                  onClick={() =>
                                    toggleOperation(op._id || op.id)
                                  }
                                >
                                  <Form.Check
                                    type="switch"
                                    id={`op-switch-${op._id || op.id}`}
                                    checked={values.operationIds.includes(
                                      op._id || op.id
                                    )}
                                    onChange={() =>
                                      toggleOperation(op._id || op.id)
                                    }
                                    className="me-2"
                                    style={{ pointerEvents: "none" }}
                                  />
                                  <span
                                    style={{ fontWeight: 500, color: "#444" }}
                                  >
                                    {op.label}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <div
                              className="text-muted mt-1"
                              style={{ fontSize: 13 }}
                            >
                              Select operations that will be assigned to this
                              module
                            </div>
                            <ErrorMessage
                              name="operationIds"
                              component="div"
                              className="text-danger"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </FilterSection>
                    <div className="d-flex justify-content-end mt-4">
                      <Button
                        variant="secondary"
                        onClick={leave}
                        className="me-2"
                        style={{ borderRadius: "30px", padding: "8px 25px" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="success"
                        type="submit"
                        disabled={isSubmitting}
                        style={{
                          backgroundColor: THEME.primary,
                          borderColor: THEME.primary,
                          borderRadius: "30px",
                          padding: "8px 30px",
                          boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        }}
                      >
                        {isSubmitting
                          ? "Processing..."
                          : isEditing
                          ? "Update Operation to Module"
                          : "Add Operation to Module"}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Form>
            );
          }}
        </Formik>
      </div>
    </>
  );
}

export default AddOM;
